import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { CloudUpload, HardDrive } from "lucide-react";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Asked before the first prompt in a conversation would leave this machine.
 * The user sees exactly where it would go and can refuse, in which case the
 * message is answered locally instead of not at all.
 */
export default function EscalationConfirmDialog({ open, plan, onSendExternally, onKeepLocal }) {
  const { t } = useTaal();
  const [dontAskAgain, setDontAskAgain] = useState(false);

  if (!plan) return null;
  const from = plan.from || {};
  const to = plan.to || {};

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <CloudUpload className="h-4 w-4 text-corona" />
            {t("Dit bericht zou je machine verlaten")}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {t("Je lokale model geeft diep werk door aan een externe aanbieder. Dit bericht valt daaronder, dus de hele tekst zou worden verstuurd naar:")}
              </p>
              <div className="rounded-lg border p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <HardDrive className="h-3.5 w-3.5" />
                  <span>{t("Nu: {aanbieder} / {model}", { aanbieder: from.provider || t("onbekend"), model: from.model || t("onbekend") })}</span>
                </div>
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <CloudUpload className="h-3.5 w-3.5 text-corona" />
                  <span>{t("Zou worden: {aanbieder} / {model}", { aanbieder: to.provider || t("onbekend"), model: to.model || t("onbekend") })}</span>
                </div>
              </div>
              {plan.escalation?.reason && (
                <p className="text-xs text-muted-foreground">{t("Reden: {reden}", { reden: plan.escalation.reason })}</p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="dont-ask-again"
                  checked={dontAskAgain}
                  onCheckedChange={(v) => setDontAskAgain(Boolean(v))}
                />
                <Label htmlFor="dont-ask-again" className="text-xs font-normal cursor-pointer">
                  {t("Niet meer vragen in dit gesprek")}
                </Label>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => { onKeepLocal(dontAskAgain); setDontAskAgain(false); }}>
            {t("Lokaal houden")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => { onSendExternally(dontAskAgain); setDontAskAgain(false); }}>
            {t("Versturen naar {aanbieder}", { aanbieder: to.provider || t("aanbieder") })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
